import { API_PREFIX_SETTING_PATH } from "@/constants/prefix-path-api"
import { FormInstance, message } from "antd"
import { AxiosRequestConfig } from "axios"
import { useState } from "react"
import { HttpClient } from "../../../../app/api/HttpClient"
import { PayloadSetting } from "../../ui-builder/types/types"

const useSettingService = () => {
  const [isLoading, setIsLoading] = useState(false)

  const getAllSettings = async (searchTerm: string = "") => {
    setIsLoading(true)
    const params: AxiosRequestConfig["params"] = {
      populate: "*",
      ...(searchTerm && {
        filters: {
          $or: [
            { id: { $containsi: searchTerm } },
            { key: { $containsi: searchTerm } },
            { value: { $containsi: searchTerm } },
            { description: { $containsi: searchTerm } },
          ],
        },
      }),
      sort: "id:DESC",
      pagination: {
        pageSize: "100",
      },
    }
    try {
      const response = await HttpClient.get(API_PREFIX_SETTING_PATH, { params, skipDomainFilter: true })
      return response
    } catch {
      message.error("Lỗi khi tải danh sách cài đặt. Vui lòng thử lại.")
    } finally {
      setIsLoading(false)
    }
  }

  const getSettingById = async (id: number) => {
    setIsLoading(true)
    const params: AxiosRequestConfig["params"] = {
      populate: "*",
    }

    if (!id) {
      message.error("Id không hợp lệ.")
      return null
    }
    try {
      const response = await HttpClient.get(`${API_PREFIX_SETTING_PATH}/${id}`, { params, skipDomainFilter: true })
      return response
    } catch {
      message.error("Lỗi khi tải thông tin cài đặt. Vui lòng thử lại.")
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const createSettings = async (PayloadSetting: PayloadSetting, form: FormInstance) => {
    setIsLoading(true)
    try {
      const response = await HttpClient.post(API_PREFIX_SETTING_PATH, {
        data: {
          key: PayloadSetting.key,
          value: PayloadSetting.value,
          description: PayloadSetting.description,
        },
      }, { skipDomainFilter: true })

      form.resetFields()
      return response
    } catch {
      message.error("Lỗi khi tạo cài đặt. Vui lòng thử lại.")
    } finally {
      setIsLoading(false)
    }
  }

  const updateSetting = async (id: number, PayloadSetting: PayloadSetting) => {
    setIsLoading(true)
    if (!id) {
      message.error("Id không hợp lệ.")
      return
    }
    try {
      const response = await HttpClient.put(`${API_PREFIX_SETTING_PATH}/${id}`, {
        data: { key: PayloadSetting.key, value: PayloadSetting.value, description: PayloadSetting.description },
      }, { skipDomainFilter: true })
      // Remove success message from service - let component handle it
      return response
    } catch {
      message.error("Lỗi khi cập nhật cài đặt. Vui lòng thử lại.")
    } finally {
      setIsLoading(false)
    }
  }

  const deleteSetting = async (id: number) => {
    setIsLoading(true)
    if (!id) {
      message.error("Id không hợp lệ.")
      return false
    }
    try {
      const response = await HttpClient.delete(`${API_PREFIX_SETTING_PATH}/${id}`, { skipDomainFilter: true })
      return true
    } catch {
      message.error("Lỗi khi xóa cài đặt. Vui lòng thử lại.")
      return false
    } finally {
      setIsLoading(false)
    }
  }

  const checkKeyExists = async (key: string) => {
    return await HttpClient.get(`${API_PREFIX_SETTING_PATH}?filters[key][$eq]=${key}`, { skipDomainFilter: true })
  }

  return {
    isLoading,
    getAllSettings,
    getSettingById,
    createSettings,
    updateSetting,
    deleteSetting,
    checkKeyExists,
  }
}

export default useSettingService
